import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { z } from 'zod';
import {
  ProfileResponseSchema,
  HandleAvailableResponseSchema,
  MeResponseSchema,
  type Onboard,
  type UpdateProfile,
} from '@web/shared';
import { apiRequest } from '@/lib/api';
import { updateSessionUser, clearSession } from '@/lib/session';

export function useProfile() {
  return useQuery({
    queryKey: ['profile'],
    queryFn: () => apiRequest('/api/profile', { schema: ProfileResponseSchema }),
  });
}

/**
 * Manual by default — the caller decides when to check (a "Check
 * availability" button, or a submit-time re-validation) rather than firing a
 * request on every keystroke. Call `.refetch()` to actually run it.
 */
export function useHandleAvailable(handle: string) {
  return useQuery({
    queryKey: ['handle', handle],
    enabled: false,
    queryFn: ({ signal }) =>
      apiRequest(`/api/profile/handle-available?handle=${encodeURIComponent(handle)}`, {
        schema: HandleAvailableResponseSchema,
        signal,
      }),
  });
}

export function useOnboard() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: Onboard) =>
      apiRequest('/api/profile/onboard', { method: 'POST', body, schema: ProfileResponseSchema }),
    onSuccess: async () => {
      const me = await apiRequest('/api/auth/me', { schema: MeResponseSchema });
      updateSessionUser(me.user);
      void qc.invalidateQueries({ queryKey: ['profile'] });
    },
  });
}

export function useUpdateProfile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpdateProfile) =>
      apiRequest('/api/profile', { method: 'PATCH', body, schema: ProfileResponseSchema }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['profile'] }),
  });
}

export function useDeleteAccount() {
  return useMutation({
    mutationFn: () =>
      apiRequest('/api/profile', { method: 'DELETE', schema: z.object({ ok: z.boolean() }) }),
    onSuccess: () => clearSession(),
  });
}
